import { create } from 'zustand';

const EMPTY_SIGNALS = {
  argos: null,
  gemic: null,
  acce: null,
};

const EMPTY_TRACKING = {
  openPositions: [],
  closedToday: [],
};

const EMPTY_MEIC = {
  dailyEquityHistory: [],
  latestEquity: null,
  error: null,
};

const EMPTY_STRATEGIES = {
  argos: null,
  gemic: null,
  acce: null,
  meic: {
    daily_equity_history: [],
    latest_equity: null,
    error: null,
  },
};

function getPositionKey(position) {
  if (!position) return null;

  const strategy = position.strategy_key || position.strategy || 'unknown';
  const id = position.id ?? 'no-id';
  const entryDate = position.entry_date || position.opened_at || 'no-entry-date';

  return `${strategy}:${id}:${entryDate}`;
}

function getSignalStatus(signal) {
  if (!signal) return null;

  return {
    status: signal.status || null,
    tradeTitle: signal.trade_title || null,
    decisionReason: signal.decision_reason || signal.no_trade_reason || null,
    tradeId: signal.trade?.id ?? null,
  };
}

function buildSnapshotChanges(previousSnapshot, nextSnapshot) {
  if (!previousSnapshot || !nextSnapshot) {
    return {
      isInitialSnapshot: true,
      openedPositions: [],
      closedPositions: [],
      removedFromOpen: [],
      signalChanges: {},
    };
  }

  const previousOpen =
    previousSnapshot.dashboard_overview?.trade_tracking?.open_positions || [];

  const nextOpen =
    nextSnapshot.dashboard_overview?.trade_tracking?.open_positions || [];

  const previousClosed =
    previousSnapshot.dashboard_overview?.trade_tracking?.closed_today || [];

  const nextClosed =
    nextSnapshot.dashboard_overview?.trade_tracking?.closed_today || [];

  const previousOpenKeys = new Set(previousOpen.map(getPositionKey));
  const nextOpenKeys = new Set(nextOpen.map(getPositionKey));
  const previousClosedKeys = new Set(previousClosed.map(getPositionKey));

  const openedPositions = nextOpen.filter(position => {
    const key = getPositionKey(position);
    return key && !previousOpenKeys.has(key);
  });

  const closedPositions = nextClosed.filter(position => {
    const key = getPositionKey(position);
    return key && !previousClosedKeys.has(key);
  });

  const removedFromOpen = previousOpen.filter(position => {
    const key = getPositionKey(position);
    return key && !nextOpenKeys.has(key);
  });

  const previousSignals = previousSnapshot.dashboard_overview?.trade_signals || {};
  const nextSignals = nextSnapshot.dashboard_overview?.trade_signals || {};

  const signalChanges = {};

  ['argos', 'gemic', 'acce'].forEach(strategyKey => {
    const previousSignal = getSignalStatus(previousSignals[strategyKey]);
    const nextSignal = getSignalStatus(nextSignals[strategyKey]);

    if (JSON.stringify(previousSignal) !== JSON.stringify(nextSignal)) {
      signalChanges[strategyKey] = {
        previous: previousSignal,
        next: nextSignal,
      };
    }
  });

  return {
    isInitialSnapshot: false,
    openedPositions,
    closedPositions,
    removedFromOpen,
    signalChanges,
  };
}

function normalizeMeicPayload(snapshot) {
  const meicPayload = snapshot?.strategies?.meic || {};

  return {
    dailyEquityHistory: meicPayload.daily_equity_history || [],
    latestEquity: meicPayload.latest_equity || null,
    error: meicPayload.error || null,
  };
}

const useStrategyDashboardStore = create((set, get) => ({
  connected: false,
  authenticated: false,
  loading: true,
  error: null,

  rawSnapshot: null,
  previousSnapshot: null,

  asofTs: null,
  marketDate: null,
  lastUpdated: null,

  dashboardOverview: null,
  summary: null,
  tradeSignals: EMPTY_SIGNALS,
  tradeTracking: EMPTY_TRACKING,
  strategies: EMPTY_STRATEGIES,

  meic: EMPTY_MEIC,

  lastSnapshotChanges: null,

  setConnected: connected => {
    set({ connected });
  },

  setAuthenticated: authenticated => {
    set({ authenticated });
  },

  setError: error => {
    set({
      error,
      loading: false,
    });
  },

  clearError: () => {
    set({ error: null });
  },

  setSnapshot: snapshot => {
    if (!snapshot || snapshot.type !== 'strategy_dashboard_snapshot') {
      console.warn('[StrategyDashboardStore] Invalid snapshot ignored:', snapshot);
      return;
    }

    const previousSnapshot = get().rawSnapshot;
    const dashboardOverview = snapshot.dashboard_overview || {};
    const tradeTracking = dashboardOverview.trade_tracking || {};
    const changes = buildSnapshotChanges(previousSnapshot, snapshot);
    const meic = normalizeMeicPayload(snapshot);

    set({
      rawSnapshot: snapshot,
      previousSnapshot,

      asofTs: snapshot.asof_ts || null,
      marketDate: snapshot.market_date || null,
      lastUpdated: new Date().toISOString(),

      dashboardOverview,
      summary: dashboardOverview.summary || null,
      tradeSignals: dashboardOverview.trade_signals || EMPTY_SIGNALS,
      tradeTracking: {
        openPositions: tradeTracking.open_positions || [],
        closedToday: tradeTracking.closed_today || [],
      },

      strategies: {
        ...EMPTY_STRATEGIES,
        ...(snapshot.strategies || {}),
      },

      meic,

      lastSnapshotChanges: changes,

      loading: false,
      error: null,
    });

    // console.log('[StrategyDashboardStore] Snapshot stored:', {
    //   asofTs: snapshot.asof_ts,
    //   marketDate: snapshot.market_date,
    //   openPositions: tradeTracking.open_positions?.length || 0,
    //   closedToday: tradeTracking.closed_today?.length || 0,
    //   meicDailyEquityRows: meic.dailyEquityHistory.length,
    //   latestMeicEquity: meic.latestEquity,
    //   changes,
    // });
  },

  requestSnapshot: () => {
    const ws = get().websocketRef;

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn('[StrategyDashboardStore] Cannot request snapshot. WS not open.');
      return;
    }

    ws.send(JSON.stringify({ type: 'REQUEST_SNAPSHOT' }));
  },

  websocketRef: null,

  setWebSocketRef: websocketRef => {
    set({ websocketRef });
  },

  reset: () => {
    set({
      connected: false,
      authenticated: false,
      loading: true,
      error: null,

      rawSnapshot: null,
      previousSnapshot: null,

      asofTs: null,
      marketDate: null,
      lastUpdated: null,

      dashboardOverview: null,
      summary: null,
      tradeSignals: EMPTY_SIGNALS,
      tradeTracking: EMPTY_TRACKING,
      strategies: EMPTY_STRATEGIES,

      meic: EMPTY_MEIC,

      lastSnapshotChanges: null,
      websocketRef: null,
    });
  },
}));

export default useStrategyDashboardStore;